import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Loader2, BellRing } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SLOTS = [
  { key: "morning", labelKey: "push_morning" },
  { key: "midday", labelKey: "push_midday" },
  { key: "coach", labelKey: "push_coach", coachOnly: true },
  { key: "evening", labelKey: "push_evening" },
  { key: "night", labelKey: "push_night" },
  { key: "blocks", labelKey: "push_blocks" },
  { key: "reengage", labelKey: "push_reengage" },
];

const DEFAULTS = { morning: true, midday: true, coach: true, evening: true, night: true, blocks: true, reengage: true };

export default function PushSettings({ open, onClose }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/push/prefs");
        setPrefs({ ...DEFAULTS, ...(data.prefs || {}) });
      } catch { setPrefs(DEFAULTS); }
    })();
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/push/prefs", { prefs });
      toast.success(t("notif_prefs_saved"));
      onClose();
    } catch { toast.error(t("err_generic")); }
    finally { setSaving(false); }
  };

  const slots = SLOTS.filter((s) => !s.coachOnly || user?.is_coach_active);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border border-white/10 text-white rounded-[16px] max-w-md" data-testid="push-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
            <BellRing className="text-[#00E676]" size={22} /> {t("notif_settings")}
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/50 text-sm -mt-1">{t("notif_settings_desc")}</p>

        <div className="space-y-1 mt-2">
          {slots.map((s) => (
            <div key={s.key} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
              <div>
                <div className="text-sm font-medium text-white/90">{t(s.labelKey)}</div>
                {s.coachOnly && <div className="text-[11px] text-white/40">{t("push_coach_note")}</div>}
              </div>
              <Switch
                data-testid={`push-switch-${s.key}`}
                checked={!!prefs[s.key]}
                onCheckedChange={(v) => setPrefs((p) => ({ ...p, [s.key]: v }))}
                className="data-[state=checked]:bg-[#00E676]"
              />
            </div>
          ))}
        </div>

        <Button data-testid="push-settings-save-btn" onClick={save} disabled={saving}
          className="w-full mt-3 bg-[#007BFF] hover:bg-[#0056b3] rounded-[12px] h-11 font-semibold">
          {saving ? <Loader2 className="animate-spin" /> : t("save")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
